import {
  Box,
  IconButton,
  Paper,
  Stack,
  Typography,
  useTheme,
} from "@mui/material";
import { tokens } from "../../../../../../../theme";
import CloseRounded from "@mui/icons-material/CloseRounded";
import { useState } from "react";
import SectionHeader from "./SectionHeader";
import { ArrowsExpandIcon } from "../../../../../../../components/icons/ArrowsExpandIcon";
import StreamModal from "./AddPreviousStreamsModal";

interface StreamExample {
  id?: string;
  title: string;
  url: string;
}

interface PreviousStreamsSectionProps {
  streams: StreamExample[];
  onStreamsChange: (streams: StreamExample[]) => void;
  venueId?: number | null;
}

export default function PreviousStreamsSection({
  streams,
  onStreamsChange,
  venueId,
}: PreviousStreamsSectionProps) {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const [modalOpen, setModalOpen] = useState(false);
  const handleGenerate = (selected: StreamExample[]) => {
    const existingIds = new Set(streams.map((s) => s.id));
    const newOnes = selected.filter((s) => !existingIds.has(s.id));
    onStreamsChange([...streams, ...newOnes]);
  };

  const removeStream = (streamId?: string) =>
    onStreamsChange(streams.filter((s) => s.id !== streamId));

  return (
    <>
      <Paper sx={{ borderRadius: "14px", p: 2.5, boxShadow: 0, mb: 2 }}>
        <SectionHeader
          title="Previous Streams Showcase"
          action="Add Stream Example"
          onAction={() => setModalOpen(true)}
        />

        {streams.length === 0 ? (
          <Box
            sx={{
              textAlign: "center",
              py: 3,
              bgcolor: "background.paper",
              borderRadius: "10px",
            }}
          >
            <Typography sx={{ fontSize: "12px", color: "text.secondary" }}>
              No streams yet. Add one to showcase previous work.
            </Typography>
          </Box>
        ) : (
          <Stack spacing={1.5}>
            {streams.map((stream) => (
              <Box
                key={stream.id}
                sx={{ display: "flex", alignItems: "center", gap: 2 }}
              >
                <Box
                  sx={{
                    flex: 1,
                    display: "flex",
                    alignItems: "center",
                    gap: 2,
                    border: `1px solid ${colors.primary[800]}`,
                    borderRadius: "10px",
                    px: 2,
                    py: 1.5,
                  }}
                >
                  <Box
                    sx={{
                      width: 56,
                      height: 44,
                      borderRadius: "6px",
                      bgcolor: colors.primary[800],
                      flexShrink: 0,
                    }}
                  />
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography
                      sx={{
                        fontSize: "12px",
                        fontWeight: 600,
                        color: colors.primary[100],
                      }}
                    >
                      {stream.title}
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: "10px",
                        color: "text.secondary",
                        mt: 0.2,
                      }}
                      noWrap
                    >
                      {stream.url}
                    </Typography>
                  </Box>
                  <IconButton
                    component="a"
                    href={stream.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{
                      color: "text.secondary",
                      p: 0.5,
                      "&:hover": { bgcolor: "background.paper" },
                    }}
                  >
                    <ArrowsExpandIcon sx={{ fontSize: 24 }} />
                  </IconButton>
                </Box>

                <IconButton
                  onClick={() => removeStream(stream.id)}
                  sx={{
                    color: "text.secondary",
                    p: 0.5,
                    "&:hover": { bgcolor: "background.paper" },
                  }}
                >
                  <CloseRounded sx={{ fontSize: 24 }} />
                </IconButton>
              </Box>
            ))}
          </Stack>
        )}
      </Paper>

      <StreamModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        onGenerate={handleGenerate}
        venueId={venueId}
      />
    </>
  );
}